/**
 * Platform staff console (#46): look up customers, view their data read-only, restore
 * deleted records and teams, suspend teams, and help users. Every read and action is
 * audited, and the customer can see it in their own activity log.
 */

import { v } from "@shared/validation.ts";
import { type ReactNode, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Nav, Row, Table } from "react-bootstrap";
import { Link, NavLink, Outlet, useParams } from "react-router";
import { StepUpProvider } from "../../components/auth/SecondFactor.tsx";
import { CheckboxField, TextField } from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { EmptyState, QueryState, Section } from "../../components/ui/kit.tsx";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { formatBytes, useFormat } from "../../lib/format.ts";
import { useApi } from "../../lib/query.ts";
import { useUrlState } from "../../lib/url-state.ts";
import { SecuritySection } from "../settings/SecuritySection.tsx";

const reasonSchema = v.object({ reason: v.string({ min: 5, max: 500 }) });
const restoreSchema = v.object({ reason: v.string({ min: 5, max: 500 }), withChildren: v.boolean() });

/** A support action: always asks for the reason that goes into the audit log. */
function ReasonButton({
  label,
  title,
  variant = "outline-primary",
  onSubmit,
  children,
}: {
  label: string;
  title: string;
  variant?: string;
  onSubmit: (reason: string) => Promise<unknown>;
  children?: ReactNode;
}) {
  return (
    <ModalFormButton
      label={label}
      title={title}
      variant={variant}
      schema={reasonSchema}
      defaults={{ reason: "" }}
      onSubmit={(val) => onSubmit(val.reason)}
    >
      {() => (
        <>
          {children}
          <TextField
            name="reason"
            label="Reason (recorded in the audit log, visible to the customer)"
            required
          />
        </>
      )}
    </ModalFormButton>
  );
}

function Json({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  if (typeof value === "object") return <code className="small">{JSON.stringify(value).slice(0, 120)}</code>;
  return <>{String(value).slice(0, 120)}</>;
}

export function StaffLayout() {
  const { me } = useAuth();
  // Set-up stays on screen after two-step verification is turned on, until the recovery codes
  // have been seen and the staff member moves on.
  const [settingUp, setSettingUp] = useState(() => !me?.mfa.enabled);
  if (!me?.isStaff)
    return (
      <Container className="py-5">
        <EmptyState icon="shield" title="Not found">
          This page doesn't exist.
        </EmptyState>
      </Container>
    );
  return (
    <div className="min-vh-100 bg-body-tertiary">
      <StepUpProvider />
      <nav className="navbar navbar-dark bg-dark px-3">
        <span className="navbar-brand">Staff console</span>
        <Nav className="me-auto">
          <Nav.Link as={NavLink} to="/staff" end>
            Customers
          </Nav.Link>
          <Nav.Link as={NavLink} to="/staff/operations">
            Email &amp; jobs
          </Nav.Link>
          <Nav.Link as={NavLink} to="/staff/audit">
            Staff audit log
          </Nav.Link>
        </Nav>
        {me.teams.length > 0 && (
          <Link to="/" className="text-white-50 small">
            Back to the app
          </Link>
        )}
      </nav>
      <Container fluid className="py-4">
        {me.mfa.enabled && !settingUp ? (
          <>
            <Alert variant="secondary" className="small">
              Everything you view or do here is recorded, and customers can see it in their activity log.
            </Alert>
            <Outlet />
          </>
        ) : (
          <div style={{ maxWidth: 720 }}>
            <Alert variant="warning">
              Staff accounts must use two-step verification. Set it up below to open the console.
            </Alert>
            <SecuritySection />
            <Button className="mt-3" disabled={!me.mfa.enabled} onClick={() => setSettingUp(false)}>
              Continue to the console
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
}

export function StaffSearchPage() {
  const { get, set } = useUrlState();
  const q = get("q");
  const [draft, setDraft] = useState(q);
  const f = useFormat();
  const results = useApi<{
    teams: Array<{
      id: string;
      name: string;
      createdAt: string;
      closedAt: string | null;
      subscriptionStatus: string;
      suspendedAt: string | null;
      members: number;
    }>;
    users: Array<{
      id: string;
      name: string;
      email: string;
      createdAt: string;
      emailVerifiedAt: string | null;
      isStaff: boolean;
    }>;
  }>(q.length >= 2 ? "/api/staff/search" : null, { q });
  return (
    <>
      <Form
        className="d-flex gap-2 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: draft });
        }}
      >
        <Form.Control
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Team name, email, name or id"
          aria-label="Search customers"
          autoFocus
        />
      </Form>
      {q.length >= 2 && (
        <QueryState query={results}>
          {(r) => (
            <Row>
              <Col lg={6}>
                <Section title="Teams" flush>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {r.teams.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <Link to={`/staff/teams/${t.id}`}>{t.name}</Link>
                            <div className="small text-muted">
                              {t.members} member{t.members === 1 ? "" : "s"} · since {f.date(t.createdAt)}
                            </div>
                          </td>
                          <td className="text-end">
                            {t.closedAt && <Badge bg="secondary">closed</Badge>}{" "}
                            {t.suspendedAt && <Badge bg="danger">suspended</Badge>}{" "}
                            <Badge bg="light" text="dark">
                              {t.subscriptionStatus}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Section>
              </Col>
              <Col lg={6}>
                <Section title="Users" flush>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {r.users.map((u) => (
                        <tr key={u.id}>
                          <td>
                            <Link to={`/staff/users/${u.id}`}>{u.name}</Link>
                            <div className="small text-muted">{u.email}</div>
                          </td>
                          <td className="text-end">
                            {!u.emailVerifiedAt && (
                              <Badge bg="warning" text="dark">
                                unverified
                              </Badge>
                            )}{" "}
                            {u.isStaff && <Badge bg="dark">staff</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Section>
              </Col>
            </Row>
          )}
        </QueryState>
      )}
    </>
  );
}

interface TeamOverview {
  team: {
    id: string;
    name: string;
    createdAt: string;
    closedAt: string | null;
    suspendedAt: string | null;
    suspendedReason: string | null;
    billingExempt: boolean;
  };
  members: Array<{
    userId: string;
    name: string;
    email: string;
    role: string;
    isOwner: boolean;
    lastLoginAt: string | null;
    mfaEnabled: boolean;
  }>;
  counts: Record<string, number>;
  billing: {
    status: string;
    access: { state: string; reason: string };
    seats: number;
    trialDaysLeft: number | null;
    storage: { usedBytes: number; allowanceBytes: number | null };
  };
  emails: Array<{ id: string; toEmail: string; subject: string; status: string; createdAt: string }>;
  audit: Array<{
    id: number;
    action: string;
    entityType: string | null;
    summary: string;
    createdAt: string;
    userName: string | null;
  }>;
  views: Record<string, string>;
}

export function StaffTeamPage() {
  const { id = "" } = useParams();
  const f = useFormat();
  const [view, setView] = useState<string | null>(null);
  const overview = useApi<TeamOverview>(`/api/staff/teams/${id}`);
  const data = useApi<{ label: string; items: Record<string, unknown>[] }>(
    view ? `/api/staff/teams/${id}/data/${view}` : null,
  );
  const deleted = useApi<
    Array<{ entityType: string; label: string; id: string; description: string; deletedAt: string }>
  >(`/api/staff/teams/${id}/deleted`);
  return (
    <QueryState query={overview}>
      {(o) => (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <h1 className="h4 mb-0 me-2">{o.team.name}</h1>
            {o.team.closedAt && <Badge bg="secondary">closed {f.date(o.team.closedAt)}</Badge>}
            {o.team.suspendedAt && <Badge bg="danger">suspended: {o.team.suspendedReason}</Badge>}
            {o.team.billingExempt && <Badge bg="info">billing exempt</Badge>}
            <div className="ms-auto d-flex gap-2">
              {o.team.closedAt && (
                <ReasonButton
                  label="Reopen team"
                  title="Reopen this team"
                  onSubmit={(reason) => api.post(`/api/staff/teams/${id}/reopen`, { reason })}
                />
              )}
              <ReasonButton
                label={o.team.suspendedAt ? "Lift suspension" : "Suspend"}
                variant={o.team.suspendedAt ? "outline-primary" : "outline-danger"}
                title={o.team.suspendedAt ? "Lift the suspension" : "Suspend this team"}
                onSubmit={(reason) =>
                  api.post(`/api/staff/teams/${id}/suspension`, { on: !o.team.suspendedAt, reason })
                }
              />
              <ReasonButton
                label={o.team.billingExempt ? "Remove billing exemption" : "Exempt from billing"}
                title="Change billing exemption"
                onSubmit={(reason) =>
                  api.post(`/api/staff/teams/${id}/billing-exempt`, { on: !o.team.billingExempt, reason })
                }
              />
            </div>
          </div>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Subscription</div>
                <div>
                  {o.billing.status} · access {o.billing.access.state} ({o.billing.access.reason})
                </div>
                {o.billing.trialDaysLeft !== null && (
                  <div className="small">{o.billing.trialDaysLeft} trial days left</div>
                )}
              </Card>
            </Col>
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Seats</div>
                <div>{o.billing.seats}</div>
              </Card>
            </Col>
            <Col md={4}>
              <Card body>
                <div className="small text-muted">Storage</div>
                <div>
                  {formatBytes(o.billing.storage.usedBytes)}
                  {o.billing.storage.allowanceBytes
                    ? ` of ${formatBytes(o.billing.storage.allowanceBytes)}`
                    : ""}
                </div>
              </Card>
            </Col>
          </Row>
          <Section title="Members" flush>
            <Table size="sm" className="mb-0">
              <tbody>
                {o.members.map((m) => (
                  <tr key={m.userId}>
                    <td>
                      <Link to={`/staff/users/${m.userId}`}>{m.name}</Link>{" "}
                      <span className="small text-muted">{m.email}</span>
                    </td>
                    <td>{m.role}</td>
                    <td className="small">{m.mfaEnabled ? "2FA on" : "no 2FA"}</td>
                    <td className="small text-muted">last sign-in {f.dateTime(m.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
          <Section title="Records (read-only)">
            <Nav variant="pills" className="mb-3 flex-wrap">
              {Object.entries(o.views).map(([key, label]) => (
                <Nav.Item key={key}>
                  <Nav.Link active={view === key} onClick={() => setView(key)}>
                    {label}{" "}
                    <Badge bg="light" text="dark">
                      {o.counts[key]}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            {view && (
              <QueryState query={data}>
                {(d) =>
                  d.items.length === 0 ? (
                    <p className="small text-muted mb-0">None.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table size="sm" className="small mb-0">
                        <thead>
                          <tr>
                            {Object.keys(d.items[0]!).map((k) => (
                              <th key={k}>{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {d.items.map((row, i) => (
                            <tr key={String(row.id ?? i)}>
                              {Object.entries(row).map(([column, val]) => (
                                <td key={column}>
                                  <Json value={val} />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )
                }
              </QueryState>
            )}
          </Section>
          <Section title="Deleted records" flush>
            <QueryState query={deleted}>
              {(rows) =>
                rows.length === 0 ? (
                  <p className="small text-muted p-3 mb-0">Nothing deleted.</p>
                ) : (
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {rows.map((r) => (
                        <tr key={`${r.entityType}:${r.id}`}>
                          <td>{r.label}</td>
                          <td>{r.description}</td>
                          <td className="small text-muted">deleted {f.dateTime(r.deletedAt)}</td>
                          <td className="text-end">
                            <ModalFormButton
                              label="Restore"
                              title={`Restore ${r.label.toLowerCase()}`}
                              schema={restoreSchema}
                              defaults={{ reason: "", withChildren: true }}
                              onSubmit={(val) =>
                                api.post(`/api/staff/teams/${id}/restore`, {
                                  entityType: r.entityType,
                                  id: r.id,
                                  ...val,
                                })
                              }
                            >
                              {() => (
                                <>
                                  <CheckboxField
                                    name="withChildren"
                                    label="Also restore what was deleted with it (e.g. a tenancy's rent history)"
                                  />
                                  <TextField
                                    name="reason"
                                    label="Reason (recorded in the audit log)"
                                    required
                                  />
                                </>
                              )}
                            </ModalFormButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )
              }
            </QueryState>
          </Section>
          <Row>
            <Col lg={6}>
              <Section title="Recent email" flush>
                <Table size="sm" className="small mb-0">
                  <tbody>
                    {o.emails.map((e) => (
                      <tr key={e.id}>
                        <td>{e.toEmail}</td>
                        <td>{e.subject}</td>
                        <td>{e.status}</td>
                        <td className="text-muted">{f.dateTime(e.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </Col>
            <Col lg={6}>
              <Section title="Team activity" flush>
                <Table size="sm" className="small mb-0">
                  <tbody>
                    {o.audit.map((a) => (
                      <tr key={a.id}>
                        <td>{a.summary || `${a.action} ${a.entityType ?? ""}`}</td>
                        <td>{a.userName}</td>
                        <td className="text-muted">{f.dateTime(a.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Section>
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}

export function StaffUserPage() {
  const { id = "" } = useParams();
  const f = useFormat();
  const q = useApi<{
    user: {
      id: string;
      name: string;
      email: string;
      createdAt: string;
      emailVerifiedAt: string | null;
      lastLoginAt: string | null;
      isActive: boolean;
      deletedAt: string | null;
      lockedUntilAt: string | null;
    };
    teams: Array<{ id: string; name: string; role: string; closedAt: string | null }>;
    mfa: { totp: boolean; passkeys: number; recoveryCodes: number };
    emailSuppression: { reason: string; createdAt: string; detail: Record<string, unknown> } | null;
  }>(`/api/staff/users/${id}`);
  return (
    <QueryState query={q}>
      {({ user, teams, mfa, emailSuppression }) => (
        <>
          <h1 className="h4">{user.name}</h1>
          <p className="text-muted">
            {user.email} · joined {f.date(user.createdAt)} ·{" "}
            {user.emailVerifiedAt ? "verified" : "not verified"} · last sign-in {f.dateTime(user.lastLoginAt)}
            {user.deletedAt && " · account deleted"}
            {user.lockedUntilAt && ` · locked until ${f.dateTime(user.lockedUntilAt)}`}
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <ReasonButton
              label="Send password reset"
              title="Send a password reset email"
              onSubmit={(reason) => api.post(`/api/staff/users/${id}/password-reset`, { reason })}
            />
            {!user.emailVerifiedAt && (
              <ReasonButton
                label="Resend verification"
                title="Resend the verification email"
                onSubmit={(reason) => api.post(`/api/staff/users/${id}/resend-verification`, { reason })}
              />
            )}
            {emailSuppression && (
              <ReasonButton
                label="Allow email again"
                title="Allow email to this address again"
                onSubmit={(reason) => api.post(`/api/staff/users/${id}/clear-email-suppression`, { reason })}
              >
                <Alert variant="warning" className="small">
                  Only once the customer confirms their mailbox works. Repeated bounces or complaints harm
                  delivery for every customer.
                </Alert>
              </ReasonButton>
            )}
            <ReasonButton
              label="Reset two-step verification"
              variant="outline-danger"
              title="Reset two-step verification"
              onSubmit={(reason) => api.post(`/api/staff/users/${id}/reset-mfa`, { reason })}
            >
              <Alert variant="warning" className="small">
                Only after checking the customer's identity another way (for example a call back to the number
                on file). This removes their authenticator app, passkeys and recovery codes and signs them
                out.
              </Alert>
            </ReasonButton>
          </div>
          {emailSuppression && (
            <Alert variant="warning">
              No emails are sent to this address: {emailSuppression.reason} on{" "}
              {f.dateTime(emailSuppression.createdAt)}.
            </Alert>
          )}
          <p>
            Two-step verification: {mfa.totp ? "authenticator app" : "no app"}, {mfa.passkeys} passkey
            {mfa.passkeys === 1 ? "" : "s"}, {mfa.recoveryCodes} recovery codes left.
          </p>
          <Section title="Teams" flush>
            <Table size="sm" className="mb-0">
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/staff/teams/${t.id}`}>{t.name}</Link>
                    </td>
                    <td>{t.role}</td>
                    <td>{t.closedAt && <Badge bg="secondary">closed</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        </>
      )}
    </QueryState>
  );
}

export function StaffOperationsPage() {
  const f = useFormat();
  const emails =
    useApi<
      Array<{
        id: string;
        toEmail: string;
        subject: string;
        template: string;
        status: string;
        error: string | null;
        createdAt: string;
      }>
    >("/api/staff/email-log");
  const jobs =
    useApi<
      Array<{
        id: number;
        type: string;
        attempts: number;
        maxAttempts: number;
        lastError: string | null;
        failedAt: string | null;
      }>
    >("/api/staff/jobs/failed");
  return (
    <Row>
      <Col xl={6}>
        <Section title="Email log (latest 100)" flush>
          <QueryState query={emails}>
            {(rows) => (
              <Table size="sm" className="small mb-0">
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id}>
                      <td>{e.toEmail}</td>
                      <td>{e.subject}</td>
                      <td>
                        {e.status}
                        {e.error && <div className="text-danger">{e.error}</div>}
                      </td>
                      <td className="text-muted">{f.dateTime(e.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </QueryState>
        </Section>
      </Col>
      <Col xl={6}>
        <Section title="Failed and retrying jobs" flush>
          <QueryState query={jobs}>
            {(rows) =>
              rows.length === 0 ? (
                <p className="small text-muted p-3 mb-0">None.</p>
              ) : (
                <Table size="sm" className="small mb-0">
                  <tbody>
                    {rows.map((j) => (
                      <tr key={j.id}>
                        <td>{j.type}</td>
                        <td>
                          {j.attempts}/{j.maxAttempts}
                        </td>
                        <td className="text-danger">{j.lastError}</td>
                        <td className="text-muted">{f.dateTime(j.failedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            }
          </QueryState>
        </Section>
      </Col>
    </Row>
  );
}

export function StaffAuditPage() {
  const f = useFormat();
  const q =
    useApi<
      Array<{
        id: number;
        action: string;
        staffName: string | null;
        teamId: string | null;
        targetUserId: string | null;
        reason: string;
        createdAt: string;
      }>
    >("/api/staff/audit");
  return (
    <Section title="Staff audit log" flush>
      <QueryState query={q}>
        {(rows) => (
          <Table size="sm" className="small mb-0">
            <thead>
              <tr>
                <th>When</th>
                <th>Staff</th>
                <th>Action</th>
                <th>Customer</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="text-nowrap">{f.dateTime(r.createdAt)}</td>
                  <td>{r.staffName ?? "system"}</td>
                  <td>{r.action}</td>
                  <td>
                    {r.teamId && <Link to={`/staff/teams/${r.teamId}`}>team</Link>}
                    {r.targetUserId && <Link to={`/staff/users/${r.targetUserId}`}> user</Link>}
                  </td>
                  <td>{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </QueryState>
    </Section>
  );
}
